import { parsePath } from 'history';

import { pendingPanels, serializePanels, skippedOn } from './onboardingPanels';

// Decides when the post-signup "preferences" lightbox may appear.
export const ONBOARDING_PREFERENCES_STATUS = {
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
};

// Deep link: any URL carrying ?onboarding=preferences asks for the prompt again.
export const ONBOARDING_QUERY_PARAM = 'onboarding';
export const ONBOARDING_QUERY_VALUE = 'preferences';

// How long to wait before asking again, after the 1st and the 2nd skip. Three asks in total.
const DAY_MS = 24 * 60 * 60 * 1000;
export const REPROMPT_DELAYS_MS = [30 * DAY_MS, 90 * DAY_MS];
export const MAX_PROMPTS = REPROMPT_DELAYS_MS.length + 1;

// "New" means the account was registered within this window.
export const NEW_USER_WINDOW_MS = 24 * 60 * 60 * 1000;

// The preferences step is a page of its own that the Premium offer hands over to.
export const ONBOARDING_PREFERENCES_PATH = '/onboarding/preferences';

// Test link: /onboarding restarts the whole flow. The links it builds carry ?force=true, which skips
// the "already asked / already chose" checks and the Premium offer's member redirect so any account
// can see the screens. Nothing links to it.
export const isForced = queryParams => queryParams?.force === 'true';

// Only ever follow an internal path: the value comes from the URL.
export const safeInternalPath = (value, fallback = '/') =>
  typeof value === 'string' && /^\/(?!\/)/.test(value) && !/[\\\s]/.test(value) ? value : fallback;

// The Premium offer's ?returnTo= is a path that may carry its own query string, so keep it whole.
export const returnToLocation = value => {
  const path = safeInternalPath(value, null);
  return path ? parsePath(path) : undefined;
};

// Where the Premium offer should send a new user who is heading to `destination`.
export const preferencesPathFor = destination => {
  const next = safeInternalPath(destination);
  return next === '/'
    ? ONBOARDING_PREFERENCES_PATH
    : `${ONBOARDING_PREFERENCES_PATH}?next=${encodeURIComponent(next)}`;
};

// True when `path` is the preferences step, with or without its ?next= query.
export const isPreferencesPath = path =>
  typeof path === 'string' &&
  (path === ONBOARDING_PREFERENCES_PATH || path.startsWith(`${ONBOARDING_PREFERENCES_PATH}?`));

// The Premium offer comes first, so the prompt waits until the reader is off these screens.
const SUPPRESSED_PATH_PREFIXES = [
  '/auth',
  '/claim',
  '/join',
  '/login',
  '/onboarding',
  '/premium',
  '/signup',
  '/subscribe',
  '/user/membership',
];

const PREFERENCE_KEYS = [
  'nutrition_diet_limit',
  'nutrition_diet_prefer',
  'culinary_topics',
  'culinary_regions',
];

const hasValues = value => Array.isArray(value) && value.length > 0;

export const hasChosenPreferences = (settings = {}) =>
  PREFERENCE_KEYS.some(key => hasValues(settings && settings[key]));

export const isNewUser = (registeredAt, now = Date.now()) => {
  const registered = registeredAt ? new Date(registeredAt).getTime() : NaN;
  return Number.isFinite(registered) && now - registered <= NEW_USER_WINDOW_MS;
};

export const isSuppressedPath = pathname =>
  SUPPRESSED_PATH_PREFIXES.some(
    prefix => pathname === prefix || (pathname || '').startsWith(`${prefix}/`),
  );

/**
 * Where the app should go after signing in or joining. The web sign-up hands a new reader to the
 * Premium offer page (/join) and on to the preferences page, but the app shows its own Premium offer
 * on top of wherever it lands, so landing on /join would leave the reader on a page the onboarding
 * keeps away from. Such a destination becomes the place they were really heading (or home).
 */
export const appDestinationAfterSignIn = destination => {
  const pathname = typeof destination === 'string' ? destination : destination?.pathname;
  if (
    typeof pathname !== 'string' ||
    !isSuppressedPath(pathname) ||
    pathname.startsWith('/onboarding')
  ) {
    return destination;
  }
  const search = typeof destination === 'object' && destination ? destination.search || '' : '';
  const inner = safeInternalPath(new URLSearchParams(search).get('returnTo'), null);
  const innerQuery = inner && inner.includes('?') ? inner.slice(inner.indexOf('?') + 1) : '';
  const heading =
    inner && isPreferencesPath(inner) ? new URLSearchParams(innerQuery).get('next') : inner;
  const target = safeInternalPath(heading, '/');
  return isSuppressedPath(parsePath(target).pathname)
    ? { pathname: '/' }
    : returnToLocation(target);
};

const toTime = value => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : NaN;
};

/**
 * Whether the reader may be asked again, given the dates they were asked before.
 * 1st ask: at signup. 2nd: a month after the 1st. 3rd: three months after the 2nd. Then never.
 */
export const isDueForPrompt = (askedAt, now = Date.now()) => {
  const asks = Array.isArray(askedAt) ? askedAt : [];
  if (asks.length === 0 || asks.length >= MAX_PROMPTS) {
    return false;
  }
  const last = toTime(asks[asks.length - 1]);
  return Number.isFinite(last) && now - last >= REPROMPT_DELAYS_MS[asks.length - 1];
};

/**
 * @param {Object} args
 * @param {boolean} args.loggedIn
 * @param {boolean} args.settingsResolved  saved settings have arrived from the server
 * @param {Object}  args.settings
 * @param {string}  args.pathname
 * @param {boolean} args.paymentModalOpen  the in-app Premium offer is on screen
 */
export const shouldPromptPreferences = ({
  loggedIn,
  settingsResolved,
  settings,
  pathname,
  paymentModalOpen = false,
  now = Date.now(),
}) => {
  if (!loggedIn || !settingsResolved || paymentModalOpen || isSuppressedPath(pathname)) {
    return false;
  }
  // Nothing outstanding: every panel was completed, so there is nothing left to ask.
  if (pendingPanels(settings).length === 0) {
    return false;
  }
  const askedAt = settings && settings.onboarding_preferences_asked_at;
  if (Array.isArray(askedAt) && askedAt.length > 0) {
    // Asked before, and something was skipped or never shown: asked again on the schedule.
    return isDueForPrompt(askedAt, now);
  }
  // Skipped or completed before ask dates were recorded: we cannot tell when, so leave them be.
  if (settings && settings.onboarding_preferences_status) {
    return false;
  }
  // Never asked: anyone is due, new or long-standing, and whatever they have saved (someone who set
  // only a diet has never seen the category and region questions). How old the account is only
  // decides how it is shown, as a full page for a new reader and a lightbox for the rest.
  return true;
};

/**
 * The overall status to save after the onboarding: completed once no panel is outstanding, skipped
 * otherwise. Kept alongside the panel record for the app versions that only know the status.
 */
export const onboardingStatusFor = settings =>
  pendingPanels(settings).length === 0
    ? ONBOARDING_PREFERENCES_STATUS.COMPLETED
    : ONBOARDING_PREFERENCES_STATUS.SKIPPED;

// What the onboarding questions edit: the diet to prefer, and the categories and regions.
const sortedIds = value => (Array.isArray(value) ? [...value].sort() : []);

export const preferenceSelection = settings => ({
  diet: sortedIds(settings && settings.nutrition_diet_prefer),
  topics: sortedIds(settings && settings.culinary_topics),
  regions: sortedIds(settings && settings.culinary_regions),
});

// Whether the reader changed anything between two snapshots: used to thank them only when they
// actually saved something, not when they skipped everything.
export const selectionChanged = (before, after) => JSON.stringify(before) !== JSON.stringify(after);

// Test links /onboarding/asked and /onboarding/reset: a way to put an account's ask history into any
// state, so the real schedule can be watched working. `?date=2026-08-01,2026-09-15` (comma
// separated, oldest first) becomes the dates the account was asked. Anything that is not a past
// date, or more dates than there are asks, is refused, and null is returned.
export const parseAskedDates = (param, now = Date.now()) => {
  if (typeof param !== 'string' || param.trim() === '') {
    return null;
  }
  const parts = param.split(',').map(part => part.trim());
  if (parts.length > MAX_PROMPTS) {
    return null;
  }
  const times = parts.map(part => (/^\d{4}-\d{2}-\d{2}/.test(part) ? Date.parse(part) : NaN));
  if (times.some(time => !Number.isFinite(time) || time > now)) {
    return null;
  }
  return times.sort((a, b) => a - b).map(time => new Date(time).toISOString());
};

// The settings that make an account look as if it was never asked, and chose nothing.
export const NEVER_ASKED_SETTINGS = {
  onboarding_preferences_status: null,
  onboarding_preferences_asked_at: [],
  onboarding_panels: '{}',
  nutrition_diet_limit: [],
  nutrition_diet_prefer: [],
  culinary_topics: [],
  culinary_regions: [],
};

// The settings for an account that was asked on these dates and skipped.
export const askedOnSettings = dates => ({
  onboarding_preferences_status: ONBOARDING_PREFERENCES_STATUS.SKIPPED,
  onboarding_preferences_asked_at: dates,
  onboarding_panels: serializePanels(skippedOn(dates)),
});
